import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  Activity,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Phone,
  Mail,
  Video,
  UserCheck
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { leadTrackingService } from '../../services/leadTrackingService'
import { formatRupeeAmount } from '../../utils/currencyFormat'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const LeadTracking = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTracking: 0,
    statusUpdates: 0,
    stageProgressions: 0,
    followUps: 0,
    meetingsScheduled: 0,
    demosCompleted: 0,
    proposalsSent: 0,
    dealsClosed: 0,
    totalValue: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Define columns for the data table
  const columns = [
    {
      key: 'trackingCode',
      label: 'Tracking Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Activity size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'lead',
      label: 'Lead',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value?.leadCode || 'N/A'}</div>
          <div className="text-sm text-gray-500">{value?.prospectName || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'trackingType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Status Update': 'bg-blue-100 text-blue-800',
          'Stage Progression': 'bg-green-100 text-green-800',
          'Follow-up': 'bg-yellow-100 text-yellow-800',
          'Meeting Scheduled': 'bg-purple-100 text-purple-800',
          'Demo Completed': 'bg-orange-100 text-orange-800',
          'Proposal Sent': 'bg-indigo-100 text-indigo-800',
          'Negotiation Started': 'bg-red-100 text-red-800',
          'Deal Closed': 'bg-green-100 text-green-800',
          'Deal Lost': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'newStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'New': 'bg-gray-100 text-gray-800',
          'Qualified': 'bg-yellow-100 text-yellow-800',
          'Contacted': 'bg-blue-100 text-blue-800',
          'Demo Scheduled': 'bg-orange-100 text-orange-800',
          'Proposal Sent': 'bg-purple-100 text-purple-800',
          'Negotiation': 'bg-red-100 text-red-800',
          'Converted': 'bg-green-100 text-green-800',
          'Lost': 'bg-red-100 text-red-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'contactMethod',
      label: 'Contact',
      sortable: true,
      render: (value) => {
        const methodIcons = {
          'Phone': <Phone size={14} className="text-green-600" />,
          'Email': <Mail size={14} className="text-blue-600" />,
          'Meeting': <Calendar size={14} className="text-purple-600" />,
          'Demo': <Video size={14} className="text-orange-600" />,
          'Video Call': <Video size={14} className="text-indigo-600" />,
          'In-Person': <UserCheck size={14} className="text-teal-600" />
        }
        return (
          <div className="flex items-center space-x-2">
            {methodIcons[value] || <Activity size={14} className="text-gray-600" />}
            <span className="text-gray-900">{value || 'N/A'}</span>
          </div>
        )
      }
    },
    {
      key: 'probability',
      label: 'Probability',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${value || 0}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600">{value || 0}%</span>
        </div>
      )
    },
    {
      key: 'trackingDate',
      label: 'Date',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ]

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [trackingResponse, statsResponse] = await Promise.all([
        leadTrackingService.getAllLeadTracking(),
        leadTrackingService.getLeadTrackingStats()
      ])

      if (trackingResponse.success) {
        setData(trackingResponse.data.trackingRecords || [])
        
        if (trackingResponse.data.trackingRecords.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No lead tracking records found. Create your first tracking record to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(trackingResponse.message || 'Failed to load lead tracking records')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load lead tracking data')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    navigate('/crm/lead-tracking/new')
  }

  const handleEdit = (row) => {
    navigate('/crm/lead-tracking/edit', { 
      state: { trackingData: row } 
    })
  }

  const handleView = (row) => {
    navigate('/crm/lead-tracking/view', { 
      state: { trackingData: row } 
    })
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await leadTrackingService.deleteLeadTracking(deleteItem._id)
      if (response.success) {
        toast.success('Lead tracking record deleted successfully')
        loadData()
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete lead tracking record')
      }
    } catch (error) {
      console.error('Error deleting lead tracking record:', error)
      toast.error('Failed to delete lead tracking record')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select lead tracking records to delete')
      return
    }

    try {
      const response = await leadTrackingService.bulkDeleteLeadTracking(selectedRows)
      if (response.success) {
        toast.success(response.message)
        setSelectedRows([])
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete lead tracking records')
      }
    } catch (error) {
      console.error('Error bulk deleting lead tracking records:', error)
      toast.error('Failed to delete lead tracking records')
    }
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Transactions',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Tracking',
      path: '/crm/lead-tracking',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Lead Tracking</h1>
          <p>Track lead progression and monitor pipeline movement</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Tracking
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTracking || 0}</div>
          <div className="metric-title">Total Tracking</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.statusUpdates || 0}</div>
          <div className="metric-title">Status Updates</div>
          <div className="metric-subtitle">Lead progression</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.meetingsScheduled || 0}</div>
          <div className="metric-title">Meetings</div>
          <div className="metric-subtitle">Scheduled</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+25%</span>
            </div>
          </div>
          <div className="metric-value">{formatRupeeAmount(stats.totalValue || 0)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">Pipeline value</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search tracking records..."
          emptyMessage="No lead tracking records found. Create your first tracking record to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Lead Tracking"
        message={`Are you sure you want to delete tracking record "${deleteItem?.trackingCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default LeadTracking
